import { useRouter } from 'expo-router';
import { useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useMutation } from 'urql';
import IdentityFields from '@/components/FormFields/IdentityFields';
import WelcomeStepFrame, {
  WelcomeStepScrollContent,
} from '@/components/Welcome/WelcomeStepFrame';
import {
  WelcomeActions,
  WelcomePrimaryAction,
} from '@/components/Welcome/WelcomeUI';
import { graphql } from '@/gql/graphql';
import useAvatarUploader from '@/hooks/useAvatarUploader';
import useUserId from '@/hooks/useUserId';
import type { WelcomeFormValues } from '@/layouts/Welcome/form';
import useJoinIntentStore from '@/stores/useJoinIntentStore';
import useOnboardingStore from '@/stores/useOnboardingStore';
import {
  getJoinKindConfiguration,
  resolveJoinMemberType,
} from '@/utils/joinKind';

const USERNAME_MIN_LENGTH = 3;
const USERNAME_MAX_LENGTH = 20;

const WelcomeUpdateProfile = graphql(/* GraphQL */ `
  mutation WelcomeUpdateProfile(
    $id: String!
    $username: String
    $firstName: String
    $lastName: String
    $color: String
    $avatarId: uuid
  ) {
    update_person_by_pk(
      pk_columns: { id: $id }
      _set: {
        first_name: $firstName
        last_name: $lastName
        username: $username
        color: $color
        avatar_id: $avatarId
      }
    ) {
      id
      first_name
      last_name
      username
      name
      color
      avatar_id
    }
  }
`);

const WelcomeCompleteProfileForInvite = graphql(/* GraphQL */ `
  mutation WelcomeCompleteProfileForInvite(
    $id: String!
    $username: String
    $firstName: String
    $lastName: String
    $color: String
    $avatarId: uuid
  ) {
    update_person_by_pk(
      pk_columns: { id: $id }
      _set: {
        first_name: $firstName
        last_name: $lastName
        username: $username
        color: $color
        avatar_id: $avatarId
        is_complete: true
      }
    ) {
      id
      first_name
      last_name
      username
      name
      color
      avatar_id
      is_complete
    }
  }
`);

export default function Welcome() {
  const { navigate, replace } = useRouter();
  const [, updateProfile] = useMutation(WelcomeUpdateProfile);
  const [, completeProfileForInvite] = useMutation(
    WelcomeCompleteProfileForInvite,
  );
  const id = useUserId();
  const { uploadAvatar } = useAvatarUploader();
  const joinIntent = useJoinIntentStore((state) => state.joinIntent);
  const joinResumePending = useJoinIntentStore(
    (state) => state.joinResumePending,
  );
  const resetOnboarding = useOnboardingStore((state) => state.reset);
  const form = useFormContext<WelcomeFormValues>();
  const {
    control,
    handleSubmit,
    setValue,
    formState: { isSubmitted, isSubmitting },
  } = form;
  const username = useWatch({ control, name: 'username' });
  const firstName = useWatch({ control, name: 'firstName' });
  const lastName = useWatch({ control, name: 'lastName' });
  const userColor = useWatch({ control, name: 'userColor' });
  const userLocalAvatarUrl = useWatch({ control, name: 'userLocalAvatarUrl' });
  const age = useWatch({ control, name: 'age' });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const trimmedUsername = username.trim();
  const isUsernameLengthValid =
    trimmedUsername.length >= USERNAME_MIN_LENGTH &&
    trimmedUsername.length <= USERNAME_MAX_LENGTH;
  const usernameValidationMessage =
    trimmedUsername.length < USERNAME_MIN_LENGTH
      ? 'Username must be at least 3 characters.'
      : trimmedUsername.length > USERNAME_MAX_LENGTH
        ? 'Username must be 20 characters or fewer.'
        : null;

  const isValid = isUsernameLengthValid && age;
  const canSubmit = isValid && !isSubmitting;

  const submitForm = handleSubmit(async (values) => {
    if (!isValid || isSubmitting) {
      return;
    }

    setErrorMessage(null);
    const uploadResult = values.userLocalAvatarUrl
      ? await uploadAvatar({
          localUrl: values.userLocalAvatarUrl,
          path: `user/${id}/profile`,
        })
      : null;
    const avatarId = uploadResult?.ok ? uploadResult.avatarId : null;

    const profileVariables = {
      id,
      username: trimmedUsername,
      firstName: values.firstName.trim() || null,
      lastName: values.lastName.trim() || null,
      color: values.userColor,
      avatarId,
    };
    const confirmedJoinIntent =
      joinResumePending && joinIntent ? joinIntent : null;

    if (confirmedJoinIntent) {
      const { data, error } = await completeProfileForInvite({
        ...profileVariables,
      });
      const completedProfileId = data?.update_person_by_pk?.id;

      if (error || !completedProfileId) {
        const isUsernameConflict = error?.graphQLErrors.some(
          (graphQLError) =>
            graphQLError.extensions?.code === 'constraint-violation' &&
            graphQLError.message.includes('person_username_key'),
        );
        setErrorMessage(
          isUsernameConflict
            ? `${trimmedUsername} appears to be taken already.`
            : 'Could not complete your profile. Please try again.',
        );
        return;
      }

      resetOnboarding();
      replace({
        pathname: getJoinKindConfiguration(confirmedJoinIntent.kind)
          .joinPathname,
        params: {
          code: confirmedJoinIntent.code,
          memberType: resolveJoinMemberType(
            confirmedJoinIntent.kind,
            confirmedJoinIntent.memberType,
          ),
        },
      });
      return;
    }

    const { error: updateProfileError } = await updateProfile(profileVariables);

    if (updateProfileError) {
      setErrorMessage(`${values.username} appears to be taken already.`);
      return;
    }

    navigate('/welcome/persona');
  });

  return (
    <WelcomeStepFrame
      title="Let's set up your identity"
      subtitle="This is how other people will see your profile."
      footer={
        <WelcomeActions>
          <WelcomePrimaryAction
            label="Continue"
            loading={isSubmitting}
            loadingLabel="Saving…"
            disabled={!canSubmit}
            onPress={submitForm}
          />
        </WelcomeActions>
      }
    >
      <WelcomeStepScrollContent>
        <IdentityFields
          age={age}
          color={userColor}
          errorMessage={
            isSubmitted
              ? usernameValidationMessage || errorMessage
              : errorMessage
          }
          firstName={firstName}
          lastName={lastName}
          localAvatarUrl={userLocalAvatarUrl}
          requireAgeConfirmation
          onChangeColor={(nextColor) => {
            setValue('userColor', nextColor, {
              shouldDirty: true,
              shouldValidate: true,
            });
          }}
          onChangeAge={(nextAge) => {
            setValue('age', nextAge, {
              shouldDirty: true,
              shouldValidate: true,
            });
          }}
          onChangeFirstName={(value) => {
            setValue('firstName', value, {
              shouldDirty: true,
              shouldValidate: true,
            });
          }}
          onChangeLastName={(value) => {
            setValue('lastName', value, {
              shouldDirty: true,
              shouldValidate: true,
            });
          }}
          onChangeLocalAvatarUrl={(nextLocalAvatarUrl) => {
            setValue('userLocalAvatarUrl', nextLocalAvatarUrl, {
              shouldDirty: true,
              shouldValidate: true,
            });
          }}
          onChangeUsername={(value) => {
            setErrorMessage(null);
            setValue('username', value, {
              shouldDirty: true,
              shouldValidate: true,
            });
          }}
          username={username}
        />
      </WelcomeStepScrollContent>
    </WelcomeStepFrame>
  );
}
